import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Boxes,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Images,
  Mail,
  MoreHorizontal,
  Pause,
  Phone,
  Star,
  Store,
  User,
  Send,
  Wallet,
  XCircle,
} from 'lucide-react';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import { string_param, use_url_state } from '@/hooks/use-url-state';
import { ago, date_short, date_time, inr, short_id } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { AuthImage, open_file_handler, open_file_in_new_tab } from '@/components/auth-image';
import { use_test_push } from '@/features/vendors/push-test';
import {
  use_request_vendor_payout,
  use_reveal_vendor_payout,
  use_verify_vendor_document,
  use_set_service_visibility,
  use_set_vendor_payout,
} from '@/features/vendors/hooks';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusBadge } from '@/features/vendors/components/status-badge';
import { ConfirmModal, ReasonModal } from '@/features/vendors/components/reason-modal';
import { PhotoLightbox } from '@/features/vendors/components/photo-lightbox';
import {
  use_approve_vendor,
  use_reject_vendor,
  use_suspend_vendor,
  use_update_scheduling,
  use_vendor_detail,
} from '@/features/vendors/hooks';
import { document_file_path, photo_file_path } from '@/features/vendors/api';
import { use_booking_list } from '@/features/bookings/hooks';
import { use_review_list } from '@/features/reviews/hooks';
import { use_support_request_list } from '@/features/support-requests/hooks';
import { VENDOR_MODULE_TYPE_LABELS } from '@/features/vendors/types';
import type { VendorServiceItem } from '@/features/vendors/types';
import { radius, radius_rows } from '@/features/vendors/radius';

type Tab = 'profile' | 'services' | 'dining' | 'documents' | 'photos' | 'activity';
const DINING_MODULES = ['CAFE', 'RESTAURANT'];

/** Tabs are module-aware: the Dining tab only shows for café/restaurant vendors. */
function tabs_for(module_types: string[]): Array<{ key: Tab; label: string }> {
  const is_dining = module_types.some((m) => DINING_MODULES.includes(m));
  return [
    { key: 'profile', label: 'Profile' },
    { key: 'services', label: 'Services' },
    ...(is_dining ? ([{ key: 'dining', label: 'Dining' }] as const) : []),
    { key: 'documents', label: 'Documents' },
    { key: 'photos', label: 'Photos' },
    { key: 'activity', label: 'Activity' },
  ];
}

export function VendorDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  // The tab lives in the URL so "look at their documents" is a link you can
  // paste into a ticket, and Back steps through tabs the way it should.
  const [tab_raw, set_tab] = use_url_state('tab', string_param);
  const [approve_open, set_approve_open] = useState(false);
  const [reject_open, set_reject_open] = useState(false);
  const [suspend_open, set_suspend_open] = useState(false);
  const [mutation_error, set_mutation_error] = useState<string | null>(null);

  const detail_q = use_vendor_detail(id);
  const approve_m = use_approve_vendor(id);
  const reject_m = use_reject_vendor(id);
  const suspend_m = use_suspend_vendor(id);
  const test_push = use_test_push(id);

  const v = detail_q.data;
  const status = v?.kyc_status;
  const available = tabs_for(v?.module_types ?? []);
  /**
   * Whether Approve is allowed, mirroring the backend rule.
   *
   * A suspended vendor that has held VERIFIED before can always be reinstated —
   * the audit trail is how we know, and it is also the only route out of
   * SUSPENDED.
   */
  const previously_verified =
    v?.kyc_status === 'SUSPENDED' && (v?.audits ?? []).some((a) => a.to_status === 'VERIFIED');
  const can_approve = previously_verified || (v?.kyc_documents.length ?? 0) > 0;
  const tab: Tab = (available.some((t) => t.key === tab_raw) ? tab_raw : 'profile') as Tab;

  function handle_approve() {
    set_mutation_error(null);
    approve_m.mutate(undefined, {
      onSuccess: () => {
        set_approve_open(false);
        notify.success(
          status === 'SUSPENDED'
            ? `${v?.business_name ?? 'Vendor'} reactivated`
            : `${v?.business_name ?? 'Vendor'} approved`,
          { description: 'They are now visible to customers and can take bookings.' },
        );
      },
      onError: (err) => {
        set_mutation_error(err instanceof ApiError ? err.message : 'Could not update this vendor');
        notify.error(err, 'Could not update this vendor.');
      },
    });
  }

  function handle_reject(reason: string) {
    set_mutation_error(null);
    reject_m.mutate(reason, {
      onSuccess: () => {
        set_reject_open(false);
        notify.success(`${v?.business_name ?? 'Vendor'} rejected`, {
          description: 'The reason is recorded in their KYC history and shown to the vendor.',
        });
      },
      onError: (err) => {
        set_mutation_error(err instanceof ApiError ? err.message : 'Could not reject this vendor');
        notify.error(err, 'Could not reject this vendor.');
      },
    });
  }

  function handle_suspend(reason: string) {
    set_mutation_error(null);
    suspend_m.mutate(reason, {
      onSuccess: () => {
        set_suspend_open(false);
        notify.success(`${v?.business_name ?? 'Vendor'} suspended`, {
          description: 'They are hidden from customer search until reactivated.',
        });
      },
      onError: (err) => {
        set_mutation_error(err instanceof ApiError ? err.message : 'Could not suspend this vendor');
        notify.error(err, 'Could not suspend this vendor.');
      },
    });
  }

  function view_document(doc_id: string) {
    void open_file_in_new_tab(document_file_path(id, doc_id)).catch((err) =>
      notify.error(err, 'Could not open this document.'),
    );
  }

  if (detail_q.error) {
    return (
      <DashboardLayout title="Vendor" breadcrumbs={[{ label: 'Not found' }]}>
        <ErrorState
          title="Couldn't load this vendor"
          description={
            detail_q.error instanceof Error ? detail_q.error.message : 'The request failed.'
          }
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => void detail_q.refetch()}>
                Try again
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <Link to="/vendors">Back to vendors</Link>
              </Button>
            </div>
          }
        />
      </DashboardLayout>
    );
  }

  /**
   * The four numbers that decide whether this vendor can actually trade.
   *
   * "Payout" is here rather than buried in the profile tab because a verified
   * vendor with no payout account is the single most common broken state on the
   * platform — 89 of 90 verified vendors, at the time of writing.
   */
  const metrics: Metric[] = [
    {
      key: 'rating',
      label: 'Rating',
      value: v && v.review_count > 0 ? `${Number(v.avg_rating).toFixed(1)} ★` : undefined,
      hint: v ? `${v.review_count} review${v.review_count === 1 ? '' : 's'}` : undefined,
      icon: Star,
      tone: 'warning',
      loading: detail_q.isLoading,
    },
    {
      key: 'services',
      label: 'Services',
      // The catalog count, matching the tab badge and the panel header. The raw
      // `services.length` includes the rows backing dining offerings and
      // grooming packages, so the tile said 10 next to a tab saying 6.
      value: v ? catalog_services(v).length : undefined,
      hint:
        v && v.services.length === 0 && v.kyc_status === 'VERIFIED'
          ? 'Verified with nothing to book'
          : undefined,
      icon: Boxes,
      tone: v && v.services.length === 0 && v.kyc_status === 'VERIFIED' ? 'warning' : 'default',
      loading: detail_q.isLoading,
    },
    {
      key: 'documents',
      label: 'KYC documents',
      value: v?.kyc_documents.length,
      icon: FileText,
      tone: v && v.kyc_documents.length === 0 ? 'warning' : 'default',
      loading: detail_q.isLoading,
    },
    {
      key: 'photos',
      label: 'Photos',
      value: v?.photos.length,
      icon: Images,
      loading: detail_q.isLoading,
    },
    {
      key: 'payout',
      label: 'Payout',
      value: v ? (v.payout ? 'On file' : 'Missing') : undefined,
      // Method plus the masked tail, which is as much as the console is allowed
      // to know — an admin console is not a reason to decrypt an account number.
      hint: v?.payout
        ? v.payout.method === 'UPI'
          ? (v.payout.upi_id_display ?? 'UPI')
          : `Bank ••${v.payout.account_number_last4 ?? '????'}`
        : 'Cannot be settled',
      icon: Wallet,
      tone: v ? (v.payout ? 'success' : 'destructive') : 'default',
      loading: detail_q.isLoading,
    },
  ];

  return (
    <DashboardLayout
      title={v?.business_name ?? 'Vendor'}
      subtitle={
        v
          ? [
              /*
               * Capped at three. A twelve-module vendor spelled every one of them
               * out, wrapped the subtitle onto a second line and pushed Reject and
               * Suspend off the header row entirely. The full list is a chip row in
               * the Business panel, which is where you would read it.
               */
              module_summary(v.module_types) || 'No modules selected',
              [v.city, v.state].filter(Boolean).join(', ') || 'No location set',
            ].join(' · ')
          : undefined
      }
      breadcrumbs={[{ label: v?.business_name ?? 'Vendor' }]}
      avatar={
        // The vendor's own photo, so the page looks like the listing a customer
        // sees rather than a generic initial.
        v && vendor_display_photo(v) ? (
          <AuthImage
            path={photo_file_path(v.id, vendor_display_photo(v)!.id, 'thumb')}
            alt=""
            className="h-14 w-14 shrink-0 rounded-full object-cover"
          />
        ) : (
          <PersonAvatar
            path={null}
            name={v?.business_name}
            seed={v?.id}
            size="lg"
            fallback_icon={Store}
          />
        )
      }
      title_badges={
        v && (
          <>
            <StatusBadge status={v.kyc_status} />
            {/*
              Only genuine *state* belongs beside the title. The missing-payout
              problem already has the page's loudest banner and its own metric
              tile; a third copy here was both redundant and the one badge long
              enough to wrap the header.
            */}
            {!v.is_active && <Badge tone="warning">Deactivated</Badge>}
          </>
        )
      }
      meta={
        v && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-data text-muted-foreground">
            {v.owner_name && (
              <span className="inline-flex items-center gap-1.5">
                <User aria-hidden="true" className="h-3.5 w-3.5" />
                {v.owner_name}
              </span>
            )}
            {v.account_phone && (
              <span className="inline-flex items-center gap-1.5">
                <Phone aria-hidden="true" className="h-3.5 w-3.5" />
                <span className="tabular">{v.account_phone}</span>
              </span>
            )}
            {v.email_for_business && (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <Mail aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{v.email_for_business}</span>
              </span>
            )}
            <span className="text-muted-foreground/70">Joined {date_short(v.created_at)}</span>
          </div>
        )
      }
      actions={
        v && (
          <>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/vendors" data-testid="back-to-list">
                <ArrowLeft className="h-4 w-4" /> Vendors
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="inline-flex h-9 items-center gap-1.5 rounded-control px-2.5 text-data font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="vendor-more-actions"
              >
                <MoreHorizontal className="h-4 w-4" />
                More
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => {
                    void navigator.clipboard.writeText(v.id);
                    notify.success('Vendor ID copied');
                  }}
                >
                  <Copy className="h-4 w-4" />
                  Copy vendor ID
                </DropdownMenuItem>
                {/*
                  Diagnostic, not a customer feature: "the vendor app isn't
                  getting notifications" otherwise has four indistinguishable
                  causes and no way to tell them apart from here. It sends a
                  real push, so it lives behind a menu rather than sitting as a
                  button next to Approve.
                */}
                <DropdownMenuItem
                  disabled={test_push.isPending}
                  onSelect={() => {
                    test_push.mutate(undefined, {
                      onSuccess: (res) => {
                        const ok = res.sent > 0;
                        const detail = [
                          `provider: ${res.provider}`,
                          `devices: ${res.devices_registered}`,
                          `delivered: ${res.sent}`,
                          res.pruned_dead_tokens > 0
                            ? `dead tokens pruned: ${res.pruned_dead_tokens}`
                            : null,
                          res.notify_push_enabled ? null : 'push disabled in their profile',
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        if (ok) notify.success(res.verdict, { description: detail });
                        else notify.warning(res.verdict, { description: detail });
                      },
                      onError: (err) => notify.error(err, 'Could not send a test push.'),
                    });
                  }}
                  data-testid="action-test-push"
                >
                  <Bell className="h-4 w-4" />
                  {test_push.isPending ? 'Sending test push…' : 'Send test push'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/*
              Approve is also the route back out of SUSPENDED — there is no
              separate unsuspend endpoint. Labelling it "Approve" on a suspended
              vendor left the suspend dialog's promise ("until manually
              unsuspended") with no visible way to keep it, so the same control
              says what it actually does in that state.
            */}
            {status !== 'VERIFIED' && (
              <Button
                variant="default"
                size="sm"
                // The backend refuses to approve a vendor with no KYC documents
                // (KYC_DOCUMENTS_REQUIRED) — reviewing nothing is not
                // reviewing. Reinstating a previously-verified suspension is
                // exempt, because that vendor has already been through the
                // gate and approve is the only way back out of SUSPENDED.
                // Letting the click through just to catch the 422 is a worse
                // way to communicate a rule than not offering the button.
                disabled={!can_approve}
                title={
                  can_approve
                    ? undefined
                    : 'This vendor has not uploaded any KYC documents yet — there is nothing to verify.'
                }
                onClick={() => set_approve_open(true)}
                data-testid="action-approve"
              >
                <CheckCircle2 className="h-4 w-4" />
                {status === 'SUSPENDED' ? 'Reactivate' : 'Approve'}
              </Button>
            )}
            {status !== 'REJECTED' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => set_reject_open(true)}
                data-testid="action-reject"
              >
                <XCircle className="h-4 w-4" /> Reject
              </Button>
            )}
            {status === 'VERIFIED' && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => set_suspend_open(true)}
                data-testid="action-suspend"
              >
                <Pause className="h-4 w-4" /> Suspend
              </Button>
            )}
          </>
        )
      }
      stats={v ? <MetricStrip metrics={metrics} /> : undefined}
      tabs={
        v ? (
          <PageTabs
            current={tab}
            on_change={set_tab}
            tabs={available.map((t) => ({
              value: t.key,
              label: t.label,
              count:
                t.key === 'services'
                  ? // The catalog count, not the raw service count: dining
                    // offerings and grooming packages are services too, and a
                    // badge saying 10 above a panel saying 6 is just a puzzle.
                    catalog_services(v).length
                  : t.key === 'documents'
                    ? v.kyc_documents.length
                    : t.key === 'photos'
                      ? v.photos.length
                      : undefined,
            }))}
          />
        ) : undefined
      }
    >
      {detail_q.isLoading && (
        <div className="console-panel p-3.5">
          <PanelSkeleton rows={8} />
        </div>
      )}

      {v && (
        <>
          {/*
            The one thing that must be visible before any tab is opened: a
            verified vendor with no payout account is trading and unpayable, and
            nobody finds that out by clicking through to a tab.
          */}
          {v.kyc_status === 'VERIFIED' && !v.payout && (
            <div
              className="flex items-start gap-2.5 rounded-panel bg-destructive/8 p-3 text-data text-destructive"
              data-testid="vendor-no-payout-notice"
            >
              <Wallet aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">This vendor cannot be paid.</p>
                <p className="text-destructive/80">
                  They are verified and taking bookings, but have no payout account on file, so a
                  settlement run will skip them.
                </p>
              </div>
            </div>
          )}

          {/*
            Submitted for review with nothing to review.
            
            The vendor app can put an application into DOCUMENTS_SUBMITTED
            without any KYC document attached, which leaves a reviewer staring
            at an empty Documents tab wondering whether the page is broken. It
            isn't — the application is, and the fix is to ask the vendor for the
            documents rather than to approve or reject blind.
          */}
          {v.kyc_status === 'DOCUMENTS_SUBMITTED' && v.kyc_documents.length === 0 && (
            <div
              className="flex items-start gap-2.5 rounded-panel bg-warning/10 p-3 text-data text-warning"
              data-testid="vendor-no-documents-notice"
            >
              <FileText aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-semibold">Submitted for review with no documents attached.</p>
                <p className="text-warning/80">
                  There is nothing here to verify. Ask the vendor to upload their KYC documents
                  before approving or rejecting.
                </p>
              </div>
            </div>
          )}

          {tab === 'profile' && <ProfileTab vendor={v} />}
          {tab === 'services' && <ServicesTab vendor={v} />}
          {tab === 'dining' && <DiningTab vendor={v} />}
          {tab === 'documents' && <DocumentsTab vendor={v} on_view_document={view_document} />}
          {tab === 'photos' && <PhotosTab vendor={v} />}
          {tab === 'activity' && <ActivityTab vendor={v} />}

          <ConfirmModal
            open={approve_open}
            title={status === 'SUSPENDED' ? 'Reactivate vendor?' : 'Approve vendor?'}
            description={
              status === 'SUSPENDED'
                ? `${v.business_name ?? 'This vendor'} returns to VERIFIED and becomes visible to customers again.`
                : `Approving ${v.business_name ?? 'this vendor'} flips KYC status to VERIFIED. The vendor will be notified.`
            }
            confirm_label={
              approve_m.isPending
                ? status === 'SUSPENDED'
                  ? 'Reactivating…'
                  : 'Approving…'
                : status === 'SUSPENDED'
                  ? 'Reactivate vendor'
                  : 'Approve vendor'
            }
            loading={approve_m.isPending}
            error={approve_m.isError ? mutation_error : null}
            on_cancel={() => set_approve_open(false)}
            on_confirm={handle_approve}
          />
          <ReasonModal
            open={reject_open}
            title="Reject vendor"
            description="The vendor will see this reason and can re-submit after fixing the issues."
            confirm_label={reject_m.isPending ? 'Rejecting…' : 'Reject vendor'}
            variant="destructive"
            loading={reject_m.isPending}
            error={reject_m.isError ? mutation_error : null}
            on_cancel={() => set_reject_open(false)}
            on_confirm={handle_reject}
          />
          <ReasonModal
            open={suspend_open}
            title="Suspend vendor"
            description="Suspended vendors are hidden from the marketplace until manually unsuspended."
            confirm_label={suspend_m.isPending ? 'Suspending…' : 'Suspend vendor'}
            variant="destructive"
            loading={suspend_m.isPending}
            error={suspend_m.isError ? mutation_error : null}
            on_cancel={() => set_suspend_open(false)}
            on_confirm={handle_suspend}
          />
        </>
      )}
    </DashboardLayout>
  );
}

/**
 * A label/value row.
 *
 * Same shape as the shared `Field`, kept as a local name because forty call
 * sites in this file use it — restyling the one function was the whole change,
 * rather than rewriting every tab.
 */
/** The first few module labels, plus a count of the rest. */
function module_summary(modules: string[]): string {
  const labels = modules.map(
    (m) => VENDOR_MODULE_TYPE_LABELS[m as keyof typeof VENDOR_MODULE_TYPE_LABELS] ?? m,
  );
  if (labels.length <= 3) return labels.join(', ');
  return labels.slice(0, 3).join(', ') + ' +' + (labels.length - 3) + ' more';
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  /*
   * Shared with the module-config panels, so a row that renders nothing is
   * treated as empty however it got that way: `<CopyableValue>` returns null for
   * a missing value, and a Row that only checked its own prop saw a perfectly
   * truthy element and printed a blank line where every neighbour showed a dash.
   */
  const empty = is_blank_value(value);
  return (
    <div className="grid grid-cols-[minmax(6rem,10rem)_1fr] gap-3 border-b border-border/60 py-2 last:border-b-0">
      <span className="text-data font-medium text-muted-foreground">{label}</span>
      <span className={cn('min-w-0 break-words text-data', empty && 'text-muted-foreground/60')}>
        {empty ? '—' : value}
      </span>
    </div>
  );
}

/**
 * Why a vendor is, or is not, findable by customers.
 *
 * These are the actual conditions in `discovery.repository` — verified, listed,
 * coordinates present, and at least one live service — not a guess at them. It
 * is the answer to the most common vendor support call there is ("we're not
 * showing up"), and until now an operator had to know the discovery SQL to give
 * it.
 */
function discovery_checks(vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>) {
  const live_services = (vendor.services ?? []).filter((s) => s.is_active).length;
  return [
    {
      key: 'verified',
      ok: vendor.kyc_status === 'VERIFIED',
      label: 'KYC verified',
      fail: `Status is ${humanize(vendor.kyc_status)} — only verified vendors are searchable.`,
    },
    {
      key: 'listed',
      ok: vendor.is_active,
      label: 'Listing active',
      fail: 'The listing is deactivated, so it is excluded from every search.',
    },
    {
      key: 'coords',
      ok: Boolean(vendor.latitude && vendor.longitude),
      label: 'Map coordinates set',
      fail: 'No latitude/longitude. Every nearby search is distance-based, so the vendor cannot appear in any of them.',
    },
    {
      key: 'services',
      ok: live_services > 0,
      label: 'At least one live service',
      fail: 'No active service, so there is nothing for a customer to book.',
    },
  ];
}

function ProfileTab({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const checks = discovery_checks(vendor);
  const failing = checks.filter((c) => !c.ok);

  const banner = vendor_banner_photo(vendor);
  const display = vendor_display_photo(vendor);
  const inset = display && display.id !== banner?.id ? display : null;
  const has_logo = vendor.photos.some((ph) => ph.type === 'LOGO');
  const has_cover = vendor.photos.some((ph) => ph.type === 'COVER');

  return (
    <div className="flex flex-col gap-gutter" data-testid="tab-content-profile">
      {/*
        What the customer actually sees, and who is behind it.

        Photos, the business and the owner's account were three separate places
        in this console, so nothing connected the picture on the listing to the
        person answering the phone. This puts them in one frame.
      */}
      <Card data-testid="brand-panel">
        <CardHeader>
          <CardTitle>Listing artwork</CardTitle>
          <CardDescription>
            {banner ? 'What a customer sees on the listing' : 'No imagery uploaded yet'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="relative">
            {/* The banner. Falls back through store front and gallery, because
                no vendor has uploaded a true COVER — see the preference list. */}
            {banner ? (
              <AuthImage
                path={photo_file_path(vendor.id, banner.id, 'medium')}
                alt=""
                className="h-36 w-full rounded-panel object-cover"
              />
            ) : (
              <div className="flex h-36 w-full items-center justify-center rounded-panel bg-muted text-data text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Images aria-hidden="true" className="h-4 w-4" /> No cover image
                </span>
              </div>
            )}

            {/*
              Logo inset over the banner — the shape every listing header in the
              app uses, so the console reads like the product.

              Skipped when the inset would be the very picture already filling
              the banner: no vendor has a real logo, so the fallback chain picks
              the store front for both, and stamping it into a circle on top of
              itself just looks like a hole punched in the banner.
            */}
            {inset && (
              <div className="absolute -bottom-5 left-4 rounded-full bg-card p-1 shadow-panel">
                <AuthImage
                  path={photo_file_path(vendor.id, inset.id, 'thumb')}
                  alt=""
                  className="h-14 w-14 rounded-full object-cover"
                />
              </div>
            )}
          </div>

          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 pt-1',
              inset && 'pl-20',
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              {/* The owner's own profile photo, beside the business. */}
              <PersonAvatar
                path={vendor.owner_avatar_url}
                name={vendor.owner_name}
                seed={vendor.owner_user_id ?? vendor.id}
                size="md"
                fallback_icon={User}
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-data font-semibold">
                  {vendor.owner_name ?? 'Owner name not set'}
                </span>
                <span className="truncate text-micro text-muted-foreground">
                  Owner{vendor.account_phone ? ` · ${vendor.account_phone}` : ''}
                </span>
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5">
              {/* Which of the two pieces of artwork the listing is missing —
                  the same facts the Photos tab warns about, said where the
                  artwork is being looked at. */}
              <Badge tone={has_logo ? 'success' : 'warning'} size="sm" dot>
                {has_logo ? 'Logo set' : 'No logo'}
              </Badge>
              <Badge tone={has_cover ? 'success' : 'warning'} size="sm" dot>
                {has_cover ? 'Cover set' : 'No cover'}
              </Badge>
              <Button variant="ghost" size="sm" asChild>
                <Link to={`/vendors/${vendor.id}?tab=photos`} data-testid="brand-open-photos">
                  {vendor.photos.length} photo{vendor.photos.length === 1 ? '' : 's'}
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ---- discoverability, because it is the question being asked ---- */}
      <Card data-testid="discoverability">
        <CardHeader>
          <CardTitle>Customer discovery</CardTitle>
          <CardDescription>
            {failing.length === 0
              ? 'Everything a nearby search requires is in place'
              : `${failing.length} of ${checks.length} requirements not met`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <div
            className={cn(
              'flex items-start gap-2.5 rounded-panel p-2.5 text-data',
              failing.length === 0
                ? 'bg-success/10 text-success'
                : 'bg-destructive/8 text-destructive',
            )}
          >
            {failing.length === 0 ? (
              <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <EyeOff aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <p className="font-semibold">
              {failing.length === 0
                ? 'This vendor can be found by customers.'
                : 'This vendor cannot be found by customers.'}
            </p>
          </div>

          <ul className="flex flex-col divide-y divide-border/60">
            {checks.map((c) => (
              <li
                key={c.key}
                className="flex items-start gap-2 py-2 first:pt-0 last:pb-0"
                data-testid={`discovery-check-${c.key}`}
              >
                {c.ok ? (
                  <CheckCircle2
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success"
                  />
                ) : (
                  <XCircle
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive"
                  />
                )}
                <span className="flex min-w-0 flex-col">
                  <span className={cn('text-data', c.ok ? '' : 'font-semibold')}>{c.label}</span>
                  {!c.ok && <span className="text-micro text-muted-foreground">{c.fail}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-micro text-muted-foreground">
            Some verticals (lab, cremation) additionally require a live service of that specific
            module.
          </p>
        </CardContent>
      </Card>

      <div className="grid items-start gap-gutter lg:grid-cols-2">
        <Card data-testid="business-profile">
          <CardHeader>
            <CardTitle>Business</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col">
            <Row label="Business name" value={vendor.business_name} />
            <Row label="Owner name" value={vendor.owner_name} />
            <Row label="Business phone" value={vendor.business_phone} />
            <Row label="Business email" value={vendor.email_for_business} />
            <Row
              label="Website"
              value={
                vendor.website_url && (
                  <a
                    href={vendor.website_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {vendor.website_url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )
              }
            />
            {/* Registration numbers exist to be pasted into a government
                portal, so they get a copy button rather than being read off
                the screen a digit at a time. */}
            <Row
              label="GST"
              value={<CopyableValue value={vendor.gst_number} label="GST number" />}
            />
            <Row
              label="Business reg #"
              value={
                <CopyableValue
                  value={vendor.business_registration_number}
                  label="Registration number"
                />
              }
            />
            <Row label="Open 24×7" value={vendor.is_24x7 ? 'Yes' : 'No'} />
            <Row
              label="Modules"
              value={<Chips items={vendor.module_types.map((m) => MODULE_LABELS[m] ?? m)} />}
            />
            {/* Which animals they will take. Stored at onboarding, never shown
                anywhere — and it is the difference between a valid booking and
                a wasted trip. */}
            {/* Always shown: "no species declared" is itself a finding for a
                vendor that is taking bookings. */}
            <Row
              label="Accepts species"
              value={
                vendor.services_species.length > 0 ? (
                  <Chips items={vendor.services_species} />
                ) : (
                  <span className="text-muted-foreground">Not declared</span>
                )
              }
            />
            {/* Genuinely optional, so they show only once filled in — three
                empty rows in a row is how a panel starts to look broken. */}
            {vendor.description && <Row label="Description" value={vendor.description} />}
            {(vendor.instagram_url || vendor.facebook_url) && (
              <Row
                label="Social"
                value={
                  <span className="flex flex-wrap items-center gap-3">
                    {vendor.instagram_url && (
                      <a
                        href={vendor.instagram_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Instagram <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {vendor.facebook_url && (
                      <a
                        href={vendor.facebook_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Facebook <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </span>
                }
              />
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col gap-gutter">
          {/*
            The person behind the business. A vendor is a User account with a
            business attached, and the console previously had no way across —
            you had to search Users by name and hope.
          */}
          <Card data-testid="owner-account">
            <CardHeader>
              <CardTitle>Owner account</CardTitle>
              <CardDescription>The person who signs in to the vendor app</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col">
              <Row label="Name" value={vendor.owner_name} />
              <Row label="Login phone" value={vendor.account_phone} />
              <Row
                label="Customer record"
                value={
                  vendor.owner_user_id ? (
                    <Link
                      to={`/users/${vendor.owner_user_id}`}
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                      data-testid="owner-account-link"
                    >
                      <User className="h-3.5 w-3.5" /> Open their account
                    </Link>
                  ) : null
                }
              />
            </CardContent>
          </Card>

          <Card data-testid="location">
            <CardHeader>
              <CardTitle>Location</CardTitle>
              <CardDescription>What nearby search matches against</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col">
              <Row label="Address" value={vendor.address} />
              <Row label="Landmark" value={vendor.landmark} />
              <Row
                label="City"
                value={[vendor.city, vendor.state].filter(Boolean).join(', ') || null}
              />
              <Row label="Pincode" value={vendor.pincode} />
              <Row
                label="Coordinates"
                value={
                  vendor.latitude && vendor.longitude ? (
                    <a
                      // Read-only jump to a map: an operator checking whether a
                      // vendor's pin is in the right city should not have to
                      // paste coordinates somewhere by hand.
                      href={`https://www.google.com/maps/search/?api=1&query=${vendor.latitude},${vendor.longitude}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 tabular text-primary hover:underline"
                      data-testid="coordinates-link"
                    >
                      {vendor.latitude}, {vendor.longitude}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-semibold text-destructive">
                      Not set — excluded from nearby search
                    </span>
                  )
                }
              />
              {radius_rows(vendor.module_details).map((r) => (
                <Row key={r.label} label={r.label} value={r.value} />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <PayoutCard vendor={vendor} />
      <SchedulingCard vendor={vendor} />
    </div>
  );
}

/** A value with a copy button, or the usual dash when it is absent. */
function CopyableValue({ value, label }: { value: string | null; label: string }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular">{value}</span>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          notify.success(`${label} copied`);
        }}
        className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

function SchedulingCard({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const update_m = use_update_scheduling(vendor.id);
  const [notice, set_notice] = useState(String(vendor.scheduling.min_notice_hours));
  const [horizon, set_horizon] = useState(String(vendor.scheduling.horizon_days));
  const [error, set_error] = useState<string | null>(null);
  const [saved, set_saved] = useState(false);

  const notice_n = Number(notice);
  const horizon_n = Number(horizon);
  // `Number('')` is 0, and 0 is a legal notice — so an empty box used to pass
  // validation and silently save "no lead time at all" while still showing
  // blank. Horizon was accidentally safe only because its floor is 1.
  const notice_valid =
    notice.trim() !== '' && Number.isInteger(notice_n) && notice_n >= 0 && notice_n <= 168;
  const horizon_valid =
    horizon.trim() !== '' && Number.isInteger(horizon_n) && horizon_n >= 1 && horizon_n <= 365;
  const dirty =
    notice_n !== vendor.scheduling.min_notice_hours || horizon_n !== vendor.scheduling.horizon_days;
  const can_save = notice_valid && horizon_valid && dirty && !update_m.isPending;

  function save() {
    set_error(null);
    set_saved(false);
    update_m.mutate(
      { min_notice_hours: notice_n, horizon_days: horizon_n },
      {
        onSuccess: () => {
          set_saved(true);
          // The inline "Saved" stays — it is next to the fields that changed —
          // but every other mutation in the console confirms with a toast, and
          // a save that only whispers next to a form field is easy to miss.
          notify.success('Scheduling updated', {
            description: `Customers can book from ${notice_n}h ahead, up to ${horizon_n} days out.`,
          });
        },
        onError: (err) => {
          const message = err instanceof ApiError ? err.message : 'Could not update scheduling';
          set_error(message);
          notify.error(err, message);
        },
      },
    );
  }

  return (
    <Card data-testid="scheduling-card">
      <CardHeader>
        <CardTitle>Scheduling</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Controls how far ahead users can book and the minimum lead time before a slot.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="min-notice-hours">Min notice (hours)</Label>
            <Input
              id="min-notice-hours"
              type="number"
              min={0}
              max={168}
              value={notice}
              onChange={(e) => set_notice(e.target.value)}
              data-testid="sched-min-notice"
            />
            <span className="text-xs text-muted-foreground">0–168 (up to 7 days)</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="horizon-days">Booking horizon (days)</Label>
            <Input
              id="horizon-days"
              type="number"
              min={1}
              max={365}
              value={horizon}
              onChange={(e) => set_horizon(e.target.value)}
              data-testid="sched-horizon"
            />
            <span className="text-xs text-muted-foreground">1–365</span>
          </div>
        </div>

        {/*
          The third scheduling rule. Read-only here: it is the vendor's own
          cancellation policy and the admin update endpoint deliberately does not
          accept it — but a reviewer looking at a "they never let me move my
          slot" complaint needs to be able to see the number.
        */}
        <p className="text-data text-muted-foreground" data-testid="sched-reschedule-notice">
          Customers must reschedule at least{' '}
          <span className="font-semibold text-foreground">
            {vendor.reschedule_min_notice_hours} hour
            {vendor.reschedule_min_notice_hours === 1 ? '' : 's'}
          </span>{' '}
          before a slot. Set by the vendor in their own app.
        </p>

        {error && (
          <p className="text-sm text-destructive" data-testid="sched-error">
            {error}
          </p>
        )}
        {saved && !dirty && (
          <p className="text-sm text-success" data-testid="sched-saved">
            Scheduling updated.
          </p>
        )}

        <div>
          <Button
            variant="gradient"
            size="sm"
            disabled={!can_save}
            onClick={save}
            data-testid="sched-save"
          >
            {update_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save scheduling
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Only walker, trainer and groomer collect a work history, so only they get the
 * timeline — rendering "None provided" under every module would be noise.
 */
const WORK_HISTORY_MODULES = ['WALKER', 'TRAINER', 'GROOMER'];

/**
 * Which uploaded photo stands for this vendor, in preference order.
 *
 * `logo_s3_key` and `cover_s3_key` exist on the profile but are null for every
 * vendor on the platform, and no vendor has uploaded a LOGO or COVER photo
 * either — so a header that only looked for a logo would show initials for all
 * 135 of them while a perfectly good store front sat unused in the Photos tab.
 * A person's face (walker, trainer) beats a building when we have one, because
 * that is what the customer is shown.
 */
const DISPLAY_PHOTO_PREFERENCE = ['LOGO', 'PROFILE_PHOTO', 'COVER', 'STORE_FRONT', 'GALLERY'];

function vendor_display_photo(vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>) {
  for (const type of DISPLAY_PHOTO_PREFERENCE) {
    const hit = vendor.photos.find((ph) => ph.type === type);
    if (hit) return hit;
  }
  return vendor.photos[0] ?? null;
}

/** The widest photo available, for the banner strip. */
function vendor_banner_photo(vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>) {
  for (const type of ['COVER', 'STORE_FRONT', 'GALLERY', 'COUNTER']) {
    const hit = vendor.photos.find((ph) => ph.type === type);
    if (hit) return hit;
  }
  return null;
}

/** Whether `ago()` gave a relative phrase rather than falling back to a date. */
function is_relative(label: string): boolean {
  return /ago$|^just now$/.test(label);
}

/**
 * Module labels, from the one list that has all of them.
 *
 * This file used to carry its own copy, which had drifted: it was missing LAB and
 * CLINIC, so a diagnostics lab's service catalog badged its module as the raw
 * enum "LAB" while the page header two inches above said "Diagnostics Lab".
 */
const MODULE_LABELS: Record<string, string> = VENDOR_MODULE_TYPE_LABELS;

/** Turn a backend enum/code (`DOG_WALKING`, `RADIUS_5_KM`) into a readable label. */
function humanize(v: string): string {
  if (v.length <= 3) return v.toUpperCase(); // language codes (en, hi…)
  return v
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function Chips({ items }: { items: string[] }) {
  if (!items || items.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((i) => (
        <span
          key={i}
          className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground"
        >
          {humanize(i)}
        </span>
      ))}
    </div>
  );
}

const yes_no = (b: boolean) => (b ? 'Yes' : 'No');
const or_dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : v;

/** One stored prior job, as the admin detail endpoint returns it. */
type VendorExperienceEntry = {
  id: string;
  role: string;
  organisation: string | null;
  /** YYYY-MM-DD. */
  from_month: string;
  to_month: string | null;
  is_current: boolean;
  description: string | null;
  months: number;
};

/** "Jun 2021" — month precision, because that is all the column stores. */
function month_label(iso: string | null): string {
  if (!iso) return 'Present';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** "3 yr 2 mo" from a whole-month count. */
function duration_label(months: number): string {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} mo`;
  if (m === 0) return `${y} yr`;
  return `${y} yr ${m} mo`;
}

/**
 * The vendor's claimed work history, for the reviewer.
 *
 * `years_of_experience` next door is a single number the vendor typed and nobody can check.
 * These are checkable claims — a named employer and a date range someone could ring up — which
 * is the difference that matters when the person will be alone with a customer's dog. The API
 * has returned this since the experience feature landed; the review screen simply never showed
 * it, so the reviewer was approving on the unverifiable number alone.
 *
 * Total is summed rather than compared against `years_of_experience` on purpose: they disagree
 * legitimately (gaps, overlaps, unlisted jobs), and flagging that as an error here would cry
 * wolf. Showing both and letting the reviewer judge is the honest presentation.
 */
function ExperienceTimeline({ entries }: { entries: VendorExperienceEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="border-t border-border pt-3" data-testid="experience-timeline-empty">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Work experience
        </p>
        <p className="mt-1 text-sm text-muted-foreground">None provided.</p>
      </div>
    );
  }
  const total = entries.reduce((sum, e) => sum + e.months, 0);
  return (
    <div className="border-t border-border pt-3" data-testid="experience-timeline">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Work experience ({entries.length})
        </p>
        <p className="text-xs text-muted-foreground">{duration_label(total)} listed in total</p>
      </div>
      <ul className="mt-2 flex flex-col gap-3">
        {entries.map((e) => (
          <li key={e.id} className="text-sm" data-testid={`experience-entry-${e.id}`}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-semibold">{e.role}</span>
              {e.organisation && <span className="text-muted-foreground">· {e.organisation}</span>}
              {e.is_current && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                  current
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {month_label(e.from_month)} – {month_label(e.to_month)} · {duration_label(e.months)}
            </div>
            {e.description && <p className="mt-1 text-xs text-muted-foreground">{e.description}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Build the labelled rows for one module's details. */
function module_rows(
  module: string,
  d: Record<string, unknown>,
): Array<{ label: string; value: React.ReactNode }> {
  const s = (k: string) => d[k] as string | null;
  const n = (k: string) => d[k] as number | null;
  const arr = (k: string) => (d[k] as string[] | undefined) ?? [];
  switch (module) {
    case 'CAFE':
      return [
        {
          label: 'Opening hours',
          value: `${or_dash(s('opening_time'))} – ${or_dash(s('closing_time'))}`,
        },
        { label: 'Years active', value: or_dash(n('years_active')) },
      ];
    case 'WALKER':
      return [
        { label: 'Full name', value: or_dash(s('full_name')) },
        { label: 'Date of birth', value: or_dash(s('date_of_birth')) },
        { label: 'Gender', value: s('gender') ? humanize(s('gender')!) : '—' },
        { label: 'Service radius', value: radius(s('service_radius')) },
        { label: 'Experience', value: `${or_dash(n('years_of_experience'))} yr` },
        { label: 'Specializations', value: <Chips items={arr('specialization_codes')} /> },
        { label: 'Languages', value: <Chips items={arr('language_codes')} /> },
      ];
    case 'TRAINER':
      return [
        { label: 'Full name', value: or_dash(s('full_name')) },
        { label: 'Date of birth', value: or_dash(s('date_of_birth')) },
        { label: 'Gender', value: s('gender') ? humanize(s('gender')!) : '—' },
        { label: 'Training mode', value: <Chips items={arr('training_mode')} /> },
        { label: 'Service radius', value: radius(s('service_radius')) },
        { label: 'Experience', value: `${or_dash(n('years_of_experience'))} yr` },
        { label: 'Trains species', value: <Chips items={arr('trainer_for_species')} /> },
      ];
    case 'GROOMER':
      return [
        { label: 'Full name', value: or_dash(s('full_name')) },
        { label: 'Salon name', value: or_dash(s('salon_name')) },
        { label: 'Date of birth', value: or_dash(s('date_of_birth')) },
        { label: 'Gender', value: s('gender') ? humanize(s('gender')!) : '—' },
        {
          label: 'Service radius',
          value: radius(s('service_radius'), { filters_discovery: false }),
        },
        { label: 'Experience', value: `${or_dash(n('years_of_experience'))} yr` },
        { label: 'Grooms species', value: <Chips items={arr('groomer_for_species')} /> },
      ];
    case 'RESTAURANT':
      return [
        { label: 'Type', value: <Chips items={arr('restaurant_type')} /> },
        {
          label: 'Delivery radius',
          value: radius(s('delivery_radius'), { filters_discovery: false }),
        },
        { label: 'FSSAI license', value: or_dash(s('fssai_license_number')) },
        {
          label: 'Opening hours',
          value: `${or_dash(s('opening_time'))} – ${or_dash(s('closing_time'))}`,
        },
        { label: 'Pet-friendly species', value: <Chips items={arr('pet_friendly_species')} /> },
        { label: 'Services', value: <Chips items={arr('services_offered')} /> },
      ];
    case 'PHARMACY':
      return [{ label: 'Drug license #', value: or_dash(s('drug_license_number')) }];
    case 'ADOPTION':
      return [
        { label: 'Organization types', value: <Chips items={arr('organization_types')} /> },
        { label: 'Contact person', value: or_dash(s('contact_person_name')) },
        { label: 'Designation', value: s('designation') ? humanize(s('designation')!) : '—' },
        { label: 'Animals available', value: or_dash(n('animals_available')) },
        { label: 'Max capacity', value: or_dash(n('max_capacity')) },
        { label: 'Volunteers', value: or_dash(n('volunteer_count')) },
        {
          label: 'Year established',
          value: s('year_established') ? humanize(s('year_established')!) : '—',
        },
        { label: 'Staff count', value: or_dash(n('staff_count')) },
      ];
    case 'CREMATION':
      return [
        { label: 'Service radius', value: radius(s('service_radius')) },
        { label: 'Home pickup', value: yes_no(Boolean(d.home_pickup_available)) },
        { label: '24×7 emergency', value: yes_no(Boolean(d.is_24x7_emergency)) },
        { label: 'Services', value: <Chips items={arr('cremation_services')} /> },
      ];
    case 'LAB':
      return [
        { label: 'Registration #', value: or_dash(s('registration_number')) },
        { label: 'Home collection', value: yes_no(Boolean(d.home_collection_available)) },
        { label: 'Service radius', value: radius(s('service_radius')) },
        { label: 'Sample types', value: <Chips items={arr('sample_types')} /> },
      ];
    case 'VET':
      return [
        { label: 'Vet name', value: or_dash(s('vet_full_name')) },
        {
          label: 'Medical degree',
          value: s('medical_degree') ? humanize(s('medical_degree')!) : '—',
        },
        { label: 'Experience', value: `${or_dash(n('years_of_experience'))} yr` },
        { label: 'Registration #', value: or_dash(s('registration_number')) },
        { label: 'Session fee', value: d.session_fee_inr ? `₹${d.session_fee_inr}` : '—' },
        { label: 'Treats species', value: <Chips items={arr('pet_types_treated')} /> },
        { label: 'Specializations', value: <Chips items={arr('specialization_codes')} /> },
      ];
    default:
      return Object.entries(d).map(([k, val]) => ({
        label: humanize(k),
        value: Array.isArray(val) ? (
          <Chips items={val as string[]} />
        ) : (
          or_dash(val as string | number | null)
        ),
      }));
  }
}

/**
 * Which services show in the catalog.
 *
 * Dining offerings and grooming packages are VendorService rows too, but they
 * surface in their own richer panels, so the catalog excludes them. Exported so
 * the tab badge counts the same thing the panel shows — the two disagreed
 * (badge 10, panel 6) and there was no way to tell which was lying.
 */
export function catalog_services(
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>,
) {
  const dining_ids = new Set((vendor.dining_offerings ?? []).map((o) => o.service_id));
  const package_ids = new Set((vendor.grooming_packages ?? []).map((p) => p.service_id));
  return (vendor.services ?? []).filter((s) => !dining_ids.has(s.id) && !package_ids.has(s.id));
}

/**
 * A module-detail row with nothing in it.
 *
 * `module_rows` renders absent values as an em dash, sometimes with a unit
 * still attached ("— yr", "— – —"), and `Chips` renders one for an empty list.
 * A vendor with twelve modules therefore produced screens of rows that said
 * nothing, which is what made the old Services tab 3,900px tall. Recognising
 * them lets the panel hide them behind a toggle instead.
 */
function is_blank_value(value: React.ReactNode): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (typeof value === 'number') return false;
  if (typeof value === 'string') {
    // Strip dashes, units-less separators and whitespace; if nothing readable
    // is left, the row is empty.
    return value.replace(/[—–\-/·\s]/g, '') === '';
  }
  if (React.isValidElement(value)) {
    if (value.type === Chips) {
      const items = (value.props as { items?: string[] }).items;
      return !items || items.length === 0;
    }
    // Renders nothing when it has nothing to copy.
    if (value.type === CopyableValue) {
      return !(value.props as { value?: string | null }).value;
    }
  }
  return false;
}

function ServicesTab({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  // `module_details` is keyed by module name at runtime; widen once here
  // rather than casting at each read below.
  const details = (vendor.module_details ?? {}) as Record<string, Record<string, unknown>>;
  const services = catalog_services(vendor);
  const grooming_packages = vendor.grooming_packages ?? [];
  const elsewhere = (vendor.services?.length ?? 0) - services.length;

  // Render in the vendor's declared module order, then any extras.
  const ordered = [
    ...vendor.module_types.filter((m) => m in details),
    ...Object.keys(details).filter((m) => !vendor.module_types.includes(m as never)),
  ];

  const [module, set_module] = useState<string>(ordered[0] ?? '');
  const [show_empty, set_show_empty] = useState(false);
  const selected = ordered.includes(module) ? module : (ordered[0] ?? '');

  if (ordered.length === 0 && services.length === 0 && grooming_packages.length === 0) {
    return (
      <Card data-testid="tab-content-services">
        <CardContent className="py-8">
          <p className="text-data text-muted-foreground">
            No services or module details yet. The vendor fills module details during onboarding and
            adds priced services from their workspace.
          </p>
        </CardContent>
      </Card>
    );
  }

  const rows = selected ? module_rows(selected, details[selected] ?? {}) : [];
  const blank_count = rows.filter((r) => is_blank_value(r.value)).length;
  const visible_rows = show_empty ? rows : rows.filter((r) => !is_blank_value(r.value));

  return (
    <div className="flex flex-col gap-gutter" data-testid="tab-content-services">
      {/* ---- priced catalog, authored post-approval in the vendor workspace ---- */}
      <Card data-testid="service-catalog">
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Service catalog ({services.length})</CardTitle>
            {elsewhere > 0 && (
              <CardDescription>
                {elsewhere} more {elsewhere === 1 ? 'service backs' : 'services back'} a dining
                offering or grooming package, shown in their own panels
              </CardDescription>
            )}
          </div>
        </CardHeader>
        {services.length === 0 ? (
          <CardContent>
            <p className="text-data text-muted-foreground">
              No priced services added yet — this vendor has nothing a customer can book.
            </p>
          </CardContent>
        ) : (
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  {[
                    ['Service', 'left'],
                    ['Module', 'left'],
                    ['Price', 'right'],
                    ['Duration', 'right'],
                    ['Bookings', 'right'],
                    ['Photos', 'left'],
                    ['Status', 'left'],
                    ['', 'right'],
                  ].map(([label, align]) => (
                    <th
                      key={label}
                      scope="col"
                      className={cn(
                        'whitespace-nowrap border-b border-border bg-muted/50 px-3 py-1.5 text-micro font-bold uppercase tracking-wide text-muted-foreground',
                        align === 'right' ? 'text-right' : 'text-left',
                      )}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr
                    key={s.id}
                    className="[&:nth-child(even)]:bg-muted/20"
                    data-testid={`service-item-${s.id}`}
                  >
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      <span className="flex min-w-0 flex-col">
                        <span className="font-semibold">{s.name}</span>
                        {s.description && (
                          <span className="line-clamp-2 max-w-[26rem] text-micro text-muted-foreground">
                            {s.description}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      {/*
                        Every module-specific field the vendor filled in. Only
                        the sample type used to appear here, so a lab test's
                        fasting rule and turnaround — both promises made to a
                        customer — were reviewable nowhere in this console.
                      */}
                      <span className="flex flex-wrap items-center gap-1">
                        <Badge tone="neutral" size="sm">
                          {MODULE_LABELS[s.module_type] ?? s.module_type}
                        </Badge>
                        {/*
                          Which pets the service is for. Shown before the
                          module-specific badges because it is the question a
                          catalogue reviewer asks of every row, not just of one
                          vertical: a dogs-only large-breed wash and one that also
                          takes cats were indistinguishable here.
                        */}
                        {(s.species ?? []).map((sp) => (
                          <Badge key={sp} tone="neutral" size="sm">
                            {humanize(sp)}
                          </Badge>
                        ))}
                        {(s.cremation_service_type ?? []).map((t) => (
                          <Badge key={t} tone="info" size="sm">
                            {humanize(t)}
                          </Badge>
                        ))}
                        {s.training_mode && (
                          <Badge tone="info" size="sm">
                            {humanize(s.training_mode)}
                          </Badge>
                        )}
                        {s.lab_sample_type && (
                          <Badge tone="info" size="sm">
                            {humanize(s.lab_sample_type)}
                          </Badge>
                        )}
                        {s.lab_fasting_required === true && (
                          <Badge tone="warning" size="sm">
                            Fasting
                          </Badge>
                        )}
                        {s.lab_turnaround_hours !== null && (
                          <Badge tone="neutral" size="sm">
                            {s.lab_turnaround_hours}h report
                          </Badge>
                        )}
                      </span>
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-data tabular font-semibold">
                      {inr(s.price_inr)}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-data tabular text-muted-foreground">
                      {s.duration_minutes ? `${s.duration_minutes} min` : '—'}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right text-data tabular">
                      {/* What is riding on this line, for anyone about to hide it. */}
                      {s.booking_count > 0 ? (
                        <Link
                          to={`/bookings?vendor_profile_id=${vendor.id}`}
                          className="font-semibold underline-offset-2 hover:text-primary hover:underline"
                        >
                          {s.booking_count}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground/60">0</span>
                      )}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      {s.images.length > 0 ? (
                        <ImageThumbs images={s.images} />
                      ) : (
                        <span className="text-muted-foreground/60">None</span>
                      )}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-data">
                      {/*
                        Inactive services were invisible here, which is the
                        wrong default: "the customer can't see my service" is a
                        support ticket, and the answer is usually that the
                        vendor switched it off.
                      */}
                      {s.is_active ? (
                        <Badge tone="success" size="sm" dot>
                          Live
                        </Badge>
                      ) : (
                        <Badge tone="neutral" size="sm" dot>
                          Hidden
                        </Badge>
                      )}
                    </td>
                    <td className="border-b border-border/50 px-3 py-2 text-right">
                      <ServiceModerationButton vendor_id={vendor.id} service={s} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---- grooming tiers ---- */}
      {grooming_packages.length > 0 && (
        <Card data-testid="grooming-packages">
          <CardHeader>
            <CardTitle>Grooming packages ({grooming_packages.length})</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2.5">
            {grooming_packages.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-panel bg-muted/40 p-2.5"
                data-testid={`grooming-package-${p.id}`}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-data font-semibold">{p.name}</span>
                    <Badge tone="primary" size="sm">
                      {humanize(p.tier)}
                    </Badge>
                    {!p.is_active && (
                      <Badge tone="neutral" size="sm">
                        Hidden
                      </Badge>
                    )}
                  </div>
                  {p.description && (
                    <p className="text-micro text-muted-foreground">{p.description}</p>
                  )}
                  {p.included_items.length > 0 && (
                    <div className="mt-1">
                      <Chips items={p.included_items} />
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-data font-semibold tabular">{inr(p.price_inr)}</p>
                  {p.mrp_inr && (
                    <p className="text-micro text-muted-foreground tabular">
                      <span className="line-through">{inr(p.mrp_inr)}</span>
                      {p.discount_pct ? ` · ${p.discount_pct}% off` : ''}
                    </p>
                  )}
                  {p.duration_minutes && (
                    <p className="text-micro text-muted-foreground tabular">
                      {p.duration_minutes} min
                    </p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ---- per-module onboarding config ---- */}
      {ordered.length > 0 && (
        <Card data-testid="module-config">
          <CardHeader className="gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>Module configuration</CardTitle>
                <CardDescription>
                  What the vendor filled in during onboarding, per module
                </CardDescription>
              </div>
              {blank_count > 0 && (
                <button
                  type="button"
                  onClick={() => set_show_empty((o) => !o)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-pill px-2.5 text-data font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid="toggle-empty-fields"
                >
                  {show_empty ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                  {show_empty ? 'Hide' : 'Show'} {blank_count} empty field
                  {blank_count === 1 ? '' : 's'}
                </button>
              )}
            </div>

            {/*
              A chip per module rather than twelve stacked panels. This vendor
              has every module the platform offers; rendering them all at once
              was the single biggest thing wrong with this page.
            */}
            {ordered.length > 1 && (
              <div className="flex flex-wrap gap-1.5" data-testid="module-picker">
                {ordered.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => set_module(m)}
                    aria-pressed={m === selected}
                    className={cn(
                      'inline-flex h-7 items-center rounded-pill px-2.5 text-data font-semibold transition-colors',
                      m === selected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary',
                    )}
                    data-testid={`module-chip-${m}`}
                  >
                    {MODULE_LABELS[m] ?? m}
                  </button>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent data-testid={`module-panel-${selected}`}>
            {visible_rows.length === 0 ? (
              <p className="text-data text-muted-foreground">
                Nothing filled in for {MODULE_LABELS[selected] ?? selected}.
              </p>
            ) : (
              <div className="grid gap-x-6 md:grid-cols-2">
                {visible_rows.map((r) => (
                  <Row key={r.label} label={r.label} value={r.value} />
                ))}
              </div>
            )}
            {/* Walker/trainer/groomer profiles carry a work history the module
                rows cannot express as a label/value pair. */}
            {WORK_HISTORY_MODULES.includes(selected) && (
              <ExperienceTimeline
                entries={
                  (details[selected]?.experience as VendorExperienceEntry[] | undefined) ?? []
                }
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const DINING_KIND_LABELS: Record<string, string> = {
  PET_CAFE: 'Pet Café',
  PET_RESTAURANT: 'Pet Restaurant',
  PET_BAKERY: 'Pet Bakery',
};
const DINING_MODE_LABELS: Record<string, string> = {
  TABLE_RESERVATION: 'Table reservation',
  ENTRY_PASS: 'Entry pass',
  EVENT: 'Event',
};

/** Read-only thumbnail strip; click opens the full image in a new tab. */
function ImageThumbs({
  images,
}: {
  images: {
    id: string;
    thumb_path: string;
    full_path: string;
    is_approved?: boolean;
  }[];
}) {
  const [error, set_error] = useState<string | null>(null);
  if (!images || images.length === 0) return null;
  const pending = images.filter((i) => i.is_approved === false).length;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="image-thumbs">
      {images.map((img) => (
        <button
          key={img.id}
          type="button"
          onClick={open_file_handler(img.full_path, set_error)}
          className={cn(
            'relative h-12 w-12 overflow-hidden rounded-md border transition-shadow hover:shadow-md',
            // A photo waiting for review is not being shown to shoppers. The
            // console rendered it identically to an approved one, so a listing
            // with an invisible picture looked completely healthy.
            img.is_approved === false ? 'border-warning ring-1 ring-warning/40' : 'border-border',
          )}
          aria-label={img.is_approved === false ? 'View image (awaiting review)' : 'View image'}
        >
          <AuthImage path={img.thumb_path} alt="" className="h-full w-full object-cover" />
        </button>
      ))}
      {pending > 0 && (
        <Badge tone="warning" size="sm" dot data-testid="image-thumbs-pending">
          {pending} awaiting review
        </Badge>
      )}
      {error && (
        <p className="w-full text-sm text-destructive" data-testid="image-thumbs-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Hide or restore one service.
 *
 * Deliberately quiet — an outline button in the last column, not a red one. The
 * catalog belongs to the vendor and this is for the rare offering they will not
 * fix themselves; before it existed the only lever an operator had over a single
 * bad line was suspending the entire business.
 *
 * The reason is mandatory because it is repeated to the vendor verbatim: a
 * service disappearing from their own console with no explanation is
 * indistinguishable from a bug.
 */
function ServiceModerationButton({
  vendor_id,
  service,
}: {
  vendor_id: string;
  service: VendorServiceItem;
}) {
  const [open, set_open] = useState(false);
  const mutation = use_set_service_visibility(vendor_id);
  const [error, set_error] = useState<string | null>(null);
  const hiding = service.is_active;

  function submit(reason: string) {
    set_error(null);
    mutation.mutate(
      { service_id: service.id, is_active: !service.is_active, reason },
      {
        onSuccess: (res) => {
          set_open(false);
          if (!res.changed) {
            notify.info('Already in that state', {
              description: `"${res.name}" was already ${res.is_active ? 'live' : 'hidden'}.`,
            });
            return;
          }
          notify.success(res.is_active ? 'Service restored' : 'Service hidden', {
            description: res.is_active
              ? `"${res.name}" is bookable again. The vendor has been told why.`
              : `"${res.name}" is no longer bookable. The vendor has been told why.`,
          });
        },
        onError: (err) => {
          const message = hiding ? 'Could not hide the service' : 'Could not restore the service';
          set_error(err instanceof ApiError ? err.message : message);
          notify.error(err, message);
        },
      },
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          set_error(null);
          set_open(true);
        }}
        data-testid={`service-moderate-${service.id}`}
      >
        {hiding ? (
          <>
            <EyeOff className="h-4 w-4" /> Hide
          </>
        ) : (
          <>
            <Eye className="h-4 w-4" /> Restore
          </>
        )}
      </Button>

      {/* The same reason-and-confirm dialog the KYC decisions use, because this
          is the same kind of act: a decision about someone's livelihood that
          they are entitled to an explanation for. */}
      <ReasonModal
        open={open}
        title={hiding ? `Hide "${service.name}"?` : `Restore "${service.name}"?`}
        description={
          hiding
            ? service.booking_count > 0
              ? `Customers will not be able to book this. It has taken ${service.booking_count} booking${service.booking_count === 1 ? '' : 's'} — those are unaffected.`
              : 'Customers will not be able to book this. It has never been booked.'
            : 'Customers will be able to book this again.'
        }
        confirm_label={hiding ? 'Hide service' : 'Restore service'}
        variant={hiding ? 'destructive' : 'default'}
        loading={mutation.isPending}
        error={error}
        on_confirm={submit}
        on_cancel={() => set_open(false)}
      />
    </>
  );
}

function CapBadge({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
        on ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground line-through'
      }`}
    >
      {label}
    </span>
  );
}

function DiningTab({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const dining = vendor.dining;
  const offerings = vendor.dining_offerings ?? [];

  return (
    <div className="flex flex-col gap-4" data-testid="tab-content-dining">
      <Card data-testid="dining-capabilities">
        <CardHeader>
          <CardTitle>Dining setup</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col">
          {dining ? (
            <>
              <Row label="Venue type" value={DINING_KIND_LABELS[dining.kind] ?? dining.kind} />
              <Row
                label="Opening hours"
                value={`${dining.opening_time} – ${dining.closing_time}`}
              />
              <Row
                label="Offers"
                value={
                  <div className="flex flex-wrap gap-1.5">
                    <CapBadge on={dining.dine_in} label="Dine-in" />
                    <CapBadge on={dining.walk_in} label="Walk-ins" />
                    <CapBadge on={dining.delivery} label="Delivery" />
                    <CapBadge on={dining.pickup} label="Pickup" />
                  </div>
                }
              />
            </>
          ) : (
            <p className="py-2 text-sm text-muted-foreground">
              This vendor hasn't saved a dining setup yet (capabilities fall back to onboarding
              defaults).
            </p>
          )}
        </CardContent>
      </Card>

      <Card data-testid="dining-offerings">
        <CardHeader>
          <CardTitle>Reservations &amp; passes ({offerings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {offerings.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookable offerings yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {offerings.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center gap-3 py-3"
                  data-testid={`dining-offering-${o.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{o.name}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                        {DINING_MODE_LABELS[o.mode] ?? o.mode}
                      </span>
                      {!o.is_active && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          inactive
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Party {o.min_party}–{o.max_party}
                      {' · '}
                      {o.mode === 'EVENT'
                        ? `${o.capacity} tickets`
                        : o.mode === 'ENTRY_PASS'
                          ? `${o.capacity} seats/session`
                          : `${o.capacity} covers`}
                      {o.turn_minutes != null ? ` · ${o.turn_minutes} min` : ''}
                      {o.event_start ? ` · ${new Date(o.event_start).toLocaleString()}` : ''}
                    </p>
                    <ImageThumbs images={o.images} />
                  </div>
                  <div className="text-right text-sm">
                    <div className="font-semibold">₹{o.price_inr}</div>
                    <div className="text-xs text-muted-foreground">
                      {o.price_unit === 'PER_GUEST' ? 'per guest' : 'flat'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function format_bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentsTab({
  vendor,
  on_view_document,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
  on_view_document: (doc_id: string) => void;
}) {
  const verify = use_verify_vendor_document(vendor.id);
  const verified_count = vendor.kyc_documents.filter((d) => d.verified_at).length;

  function toggle(doc_id: string, verified: boolean) {
    verify.mutate(
      { doc_id, verified },
      {
        onSuccess: () =>
          notify.success(verified ? 'Document marked verified' : 'Verification removed'),
        onError: (err) => notify.error(err, 'Could not update the document'),
      },
    );
  }

  return (
    <Card data-testid="tab-content-documents">
      <CardHeader>
        <CardTitle>KYC documents ({vendor.kyc_documents.length})</CardTitle>
        {vendor.kyc_documents.length > 0 && (
          /*
            Ticking a document does NOT approve the vendor, and an operator seeing
            a per-row control on a KYC screen will reasonably assume it does. Said
            once here rather than in a tooltip nobody opens.
          */
          <CardDescription>
            {verified_count} of {vendor.kyc_documents.length} marked as read. This is a record of
            who checked what — approving the vendor is the separate action in the header.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {vendor.kyc_documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {vendor.kyc_documents.map((d) => (
              <li
                key={d.id}
                data-testid={`kyc-doc-${d.id}`}
                className="flex items-center gap-3 py-3"
              >
                {d.mime_type.startsWith('image/') ? (
                  <button
                    type="button"
                    onClick={() => on_view_document(d.id)}
                    className="shrink-0 overflow-hidden rounded-md border border-border transition-shadow hover:shadow-md"
                    aria-label={`View ${d.original_filename}`}
                  >
                    <AuthImage
                      path={document_file_path(vendor.id, d.id)}
                      alt={d.original_filename}
                      className="h-14 w-14 object-cover"
                    />
                  </button>
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-muted">
                    <FileText className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-1.5 text-data font-semibold">
                    {/* `OWNER_ID_PROOF` is a database value, not a label. */}
                    {humanize(d.type)}
                    {d.verified_at ? (
                      <Badge tone="success" size="sm" dot>
                        Verified {date_short(d.verified_at)}
                        {d.verified_by_name ? ` by ${d.verified_by_name}` : ''}
                      </Badge>
                    ) : (
                      /*
                        Said out loud rather than left blank. A document nobody
                        has marked verified is the normal state on this
                        platform, and an admin reviewing KYC needs to know which
                        of the two they are looking at.
                      */
                      <Badge tone="neutral" size="sm">
                        Not marked verified
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-micro text-muted-foreground">
                    {d.original_filename} · {format_bytes(d.size_bytes)} ·{' '}
                    {d.mime_type.replace('application/', '').toUpperCase()} · uploaded{' '}
                    {date_short(d.uploaded_at)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant={d.verified_at ? 'ghost' : 'outline'}
                    size="sm"
                    disabled={verify.isPending}
                    onClick={() => toggle(d.id, !d.verified_at)}
                    data-testid={`verify-doc-${d.id}`}
                  >
                    {d.verified_at ? (
                      <>
                        <XCircle className="h-4 w-4" /> Unmark
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4" /> Mark verified
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => on_view_document(d.id)}
                    data-testid={`view-doc-${d.id}`}
                  >
                    <ExternalLink className="h-4 w-4" /> View
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Photo purposes, in the order a reviewer wants them.
 *
 * `VendorPhotoType` is a database enum, and the tab was printing it raw
 * (`STORE_FRONT`) under every tile with no grouping — so a vendor with a logo,
 * a cover and six gallery shots looked like eight interchangeable pictures. The
 * reviewer's question is "do they have the ones that matter", which needs the
 * purposes named and separated.
 */
const PHOTO_TYPE_LABELS: Record<string, string> = {
  LOGO: 'Logo',
  COVER: 'Cover image',
  PROFILE_PHOTO: 'Profile photo (person)',
  STORE_FRONT: 'Store front',
  COUNTER: 'Counter',
  STORAGE: 'Storage',
  GALLERY: 'Gallery',
};

const PHOTO_TYPE_ORDER = [
  'LOGO',
  'COVER',
  'PROFILE_PHOTO',
  'STORE_FRONT',
  'COUNTER',
  'STORAGE',
  'GALLERY',
];

function PhotosTab({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const [lightbox_index, set_lightbox_index] = useState<number | null>(null);

  // Grouped, but the lightbox indexes the flat list — so build the flat order
  // from the groups and index into that, or opening a tile shows a different
  // picture than the one clicked.
  const groups = PHOTO_TYPE_ORDER.map((type) => ({
    type,
    photos: vendor.photos.filter((p) => p.type === type),
  }))
    .concat([
      {
        type: 'OTHER',
        photos: vendor.photos.filter((p) => !PHOTO_TYPE_ORDER.includes(p.type)),
      },
    ])
    .filter((g) => g.photos.length > 0);

  const flat = groups.flatMap((g) => g.photos);
  const index_of = (id: string) => flat.findIndex((p) => p.id === id);

  const has_logo = vendor.photos.some((p) => p.type === 'LOGO');
  const has_cover = vendor.photos.some((p) => p.type === 'COVER');

  return (
    <div className="flex flex-col gap-gutter" data-testid="tab-content-photos">
      {/*
        A verified vendor with no logo or cover renders as a blank card in the
        customer app. That is a listing-quality problem an admin can chase, and
        it was invisible here.
      */}
      {vendor.kyc_status === 'VERIFIED' && (!has_logo || !has_cover) && (
        <div
          className="flex items-start gap-2.5 rounded-panel bg-warning/10 p-3 text-data text-warning"
          data-testid="photos-missing-notice"
        >
          <Images aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Missing{' '}
              {[!has_logo && 'a logo', !has_cover && 'a cover image'].filter(Boolean).join(' and ')}
              .
            </p>
            <p className="text-warning/80">
              This vendor is live, so their listing shows to customers without artwork.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Photos ({vendor.photos.length})</CardTitle>
          <CardDescription>
            {groups.length === 0
              ? 'Nothing uploaded'
              : groups
                  .map((g) => `${PHOTO_TYPE_LABELS[g.type] ?? humanize(g.type)} ${g.photos.length}`)
                  .join(' · ')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {vendor.photos.length === 0 ? (
            <p className="text-data text-muted-foreground">
              No photos uploaded yet. Listings without images convert poorly.
            </p>
          ) : (
            groups.map((g) => (
              <div
                key={g.type}
                className="flex flex-col gap-2"
                data-testid={`photo-group-${g.type}`}
              >
                <p className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
                  {PHOTO_TYPE_LABELS[g.type] ?? humanize(g.type)} ({g.photos.length})
                </p>
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {g.photos.map((p) => (
                    <li key={p.id} data-testid={`photo-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => set_lightbox_index(index_of(p.id))}
                        data-testid={`photo-open-${p.id}`}
                        className="group flex w-full flex-col overflow-hidden rounded-panel bg-muted/40 text-left shadow-panel transition-shadow hover:shadow-panel-hover"
                        aria-label={`View ${p.original_filename}`}
                      >
                        <AuthImage
                          path={photo_file_path(vendor.id, p.id, 'thumb')}
                          alt={p.original_filename}
                          className="aspect-square w-full object-cover transition-transform group-hover:scale-[1.02]"
                        />
                        <span className="flex flex-col p-2">
                          <span className="truncate text-micro font-semibold">
                            {p.original_filename}
                          </span>
                          <span className="truncate text-micro text-muted-foreground">
                            {format_bytes(p.size_bytes)} · #{p.display_order} ·{' '}
                            {date_short(p.uploaded_at)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}

          {lightbox_index !== null && (
            <PhotoLightbox
              vendor_id={vendor.id}
              // The grouped order, so opening a tile shows the tile that was
              // clicked rather than whatever sat at that index in the raw list.
              photos={flat}
              index={lightbox_index}
              on_close={() => set_lightbox_index(null)}
              on_navigate={set_lightbox_index}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Activity: what has happened to this vendor, and what they have been doing.
 *
 * The old tab was the KYC audit list and nothing else, with raw
 * `toLocaleString()` timestamps and no indication of who made each decision —
 * so "why was this vendor suspended, and by whom" was unanswerable from the one
 * screen built to answer it.
 *
 * Bookings and reviews come from the existing list endpoints, which already
 * accept `vendor_profile_id`; no new backend was needed to put a vendor's
 * actual trading activity on their own page.
 */
function ActivityTab({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const bookings = use_booking_list({ vendor_profile_id: vendor.id, page_size: 5 });
  const reviews = use_review_list({ vendor_profile_id: vendor.id, page_size: 5 });
  // Complaints raised against this vendor — no-shows and reassignments. The
  // endpoint already accepts `vendor_profile_id`; nothing here needed building.
  const support = use_support_request_list({ vendor_profile_id: vendor.id, page_size: 5 });

  return (
    <div className="flex flex-col gap-gutter" data-testid="tab-content-activity">
      <Card data-testid="kyc-history">
        <CardHeader>
          <CardTitle>KYC history ({vendor.audits.length})</CardTitle>
          <CardDescription>Every status change, who made it and why</CardDescription>
        </CardHeader>
        <CardContent>
          {vendor.audits.length === 0 ? (
            <p className="text-data text-muted-foreground">No status changes yet.</p>
          ) : (
            <ol className="flex flex-col">
              {vendor.audits.map((a, i) => {
                const last = i === vendor.audits.length - 1;
                return (
                  <li key={a.id} className="flex gap-2.5" data-testid={`audit-${a.id}`}>
                    {/* A rail, so the sequence reads as a sequence. */}
                    <span className="flex flex-col items-center pt-1.5">
                      <span
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          a.to_status === 'VERIFIED'
                            ? 'bg-success'
                            : a.to_status === 'REJECTED'
                              ? 'bg-destructive'
                              : a.to_status === 'SUSPENDED'
                                ? 'bg-warning'
                                : 'bg-muted-foreground/50',
                        )}
                      />
                      {!last && <span className="w-px flex-1 bg-border" />}
                    </span>
                    <span
                      className={cn('flex min-w-0 flex-1 flex-col gap-0.5', last ? '' : 'pb-3')}
                    >
                      <span className="flex flex-wrap items-center gap-1.5">
                        {a.from_status ? (
                          <>
                            <StatusBadge status={a.from_status} />
                            <ArrowRight
                              aria-hidden="true"
                              className="h-3 w-3 text-muted-foreground"
                            />
                          </>
                        ) : (
                          <span className="text-micro font-semibold uppercase text-muted-foreground">
                            Initial
                          </span>
                        )}
                        <StatusBadge status={a.to_status} />
                        {/*
                          `ago` falls back to an absolute date past a week, so
                          appending it unconditionally printed the same date
                          twice ("10 Aug 2026, 11:47 am · 10 Aug 2026").
                        */}
                        <span className="ml-auto whitespace-nowrap text-micro text-muted-foreground">
                          {date_time(a.changed_at)}
                          {is_relative(ago(a.changed_at)) ? ` · ${ago(a.changed_at)}` : ''}
                        </span>
                      </span>
                      <span className="text-micro text-muted-foreground">
                        by {a.changed_by_name ?? `admin ${short_id(a.changed_by_id, 8)}`}
                      </span>
                      {a.reason && (
                        <span className="text-data" data-testid={`audit-reason-${a.id}`}>
                          “{a.reason}”
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </CardContent>
      </Card>

      <div className="grid items-start gap-gutter lg:grid-cols-2">
        <Card data-testid="vendor-recent-bookings">
          <CardHeader className="flex-row items-center justify-between gap-3">
            <div>
              <CardTitle>Recent bookings</CardTitle>
              {/* The API orders by booking time, not by slot, and the row
                  leads with the slot — so say which order this is in. */}
              <CardDescription>
                {bookings.data
                  ? `${bookings.data.total} in total · most recently booked first`
                  : 'Loading…'}
              </CardDescription>
            </div>
            {bookings.data && bookings.data.total > 0 && (
              <Button variant="ghost" size="sm" asChild>
                <Link
                  to={`/bookings?vendor_profile_id=${vendor.id}`}
                  data-testid="vendor-view-all-bookings"
                >
                  View all
                </Link>
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {bookings.isLoading ? (
              <PanelSkeleton rows={4} />
            ) : bookings.error ? (
              <p className="text-data text-destructive">Could not load bookings.</p>
            ) : (bookings.data?.items.length ?? 0) === 0 ? (
              <p className="text-data text-muted-foreground">
                No bookings yet. A verified vendor with no bookings may not be discoverable.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border/60">
                {bookings.data?.items.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="flex min-w-0 flex-col">
                      <Link
                        to={`/bookings/${b.id}`}
                        className="truncate text-data font-medium underline-offset-2 hover:text-primary hover:underline"
                      >
                        {date_time(b.slot_start)}
                      </Link>
                      <span className="truncate text-micro text-muted-foreground">
                        {humanize(b.module_type)} · {b.user_name ?? b.user_phone ?? 'Customer'}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-0.5">
                      <span className="text-data tabular font-semibold">{inr(b.price_inr)}</span>
                      <Badge
                        tone={
                          b.status === 'COMPLETED'
                            ? 'success'
                            : b.status === 'CANCELLED' || b.status === 'NO_SHOW'
                              ? 'destructive'
                              : 'info'
                        }
                        size="sm"
                      >
                        {humanize(b.status)}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="vendor-recent-reviews">
          <CardHeader className="flex-row items-center justify-between gap-3">
            <div>
              <CardTitle>Recent reviews</CardTitle>
              <CardDescription>
                {vendor.review_count > 0
                  ? `${Number(vendor.avg_rating).toFixed(1)} average from ${vendor.review_count}`
                  : 'No reviews yet'}
              </CardDescription>
            </div>
            {vendor.review_count > 0 && (
              <Button variant="ghost" size="sm" asChild>
                <Link
                  to={`/reviews?vendor_profile_id=${vendor.id}`}
                  data-testid="vendor-view-all-reviews"
                >
                  View all
                </Link>
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {reviews.isLoading ? (
              <PanelSkeleton rows={3} />
            ) : reviews.error ? (
              <p className="text-data text-destructive">Could not load reviews.</p>
            ) : (reviews.data?.items.length ?? 0) === 0 ? (
              <p className="text-data text-muted-foreground">No customer has reviewed them yet.</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border/60">
                {reviews.data?.items.map((r) => (
                  <li key={r.id} className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center gap-0.5"
                        aria-label={`${r.rating} out of 5`}
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            aria-hidden="true"
                            className={cn(
                              'h-3 w-3',
                              n <= r.rating
                                ? 'fill-warning text-warning'
                                : 'text-muted-foreground/40',
                            )}
                          />
                        ))}
                      </span>
                      <span className="text-data font-medium">{r.reviewer_name}</span>
                      <span className="text-micro text-muted-foreground">{ago(r.created_at)}</span>
                      {r.is_hidden && (
                        <Badge tone="neutral" size="sm">
                          Hidden
                        </Badge>
                      )}
                      {r.reported_at && (
                        <Badge tone="destructive" size="sm">
                          Reported
                        </Badge>
                      )}
                    </span>
                    {r.comment && <p className="text-data text-muted-foreground">{r.comment}</p>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/*
        Complaints against this vendor.

        A no-show or a reassignment request is the strongest signal a listing is
        going wrong, and it lived in a different module keyed by booking — so
        "has anyone complained about this vendor" took a filtered search in
        Support requests that an operator had to think to run.
      */}
      <Card data-testid="vendor-support-requests">
        <CardHeader className="flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>Support requests</CardTitle>
            <CardDescription>
              {support.data ? `${support.data.total} raised against this vendor` : 'Loading…'}
            </CardDescription>
          </div>
          {support.data && support.data.total > 0 && (
            <Button variant="ghost" size="sm" asChild>
              <Link
                to={`/support-requests?vendor_profile_id=${vendor.id}`}
                data-testid="vendor-view-all-support"
              >
                View all
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {support.isLoading ? (
            <PanelSkeleton rows={3} />
          ) : support.error ? (
            <p className="text-data text-destructive">Could not load support requests.</p>
          ) : (support.data?.items.length ?? 0) === 0 ? (
            <p className="text-data text-muted-foreground">
              No complaints raised against this vendor.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border/60">
              {support.data?.items.map((r) => (
                <li key={r.id} className="flex flex-col gap-1 py-2 first:pt-0 last:pb-0">
                  <span className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/support-requests/${r.id}`}
                      className="text-data font-semibold underline-offset-2 hover:text-primary hover:underline"
                    >
                      {humanize(r.kind)}
                    </Link>
                    <Badge
                      tone={
                        r.status === 'RESOLVED'
                          ? 'success'
                          : r.status === 'OPEN'
                            ? 'warning'
                            : r.status === 'CANCELLED'
                              ? 'neutral'
                              : 'info'
                      }
                      size="sm"
                      dot
                    >
                      {humanize(r.status)}
                    </Badge>
                    <span className="text-micro text-muted-foreground">
                      {humanize(r.module_type)} · {ago(r.created_at)}
                    </span>
                    {r.refund_amount_inr && Number(r.refund_amount_inr) > 0 && (
                      <Badge tone="destructive" size="sm">
                        Refunded {inr(r.refund_amount_inr)}
                      </Badge>
                    )}
                  </span>
                  {r.reason && <p className="text-data text-muted-foreground">“{r.reason}”</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Read-only view of a vendor's payout destination.
 *
 * Admins get a notification whenever a vendor repoints their payouts, and a notification with
 * nowhere to go is noise — the link on that notification lands here. Read-only on purpose: this
 * is the vendor's money and their decision, and an admin quietly editing it would be
 * indistinguishable from the takeover the 30-day cooldown exists to slow down.
 */
/**
 * A masked payout value with a Copy button that fetches the real one.
 *
 * The real value is never rendered and never stored — it lives between the response
 * landing and `clipboard.writeText`, then goes out of scope. That is deliberate:
 * payout details on screen end up in screenshots, and screenshots of this console
 * get pasted into group chats.
 *
 * Every copy writes an audit row naming the admin, which is the trade that makes
 * reading someone's bank account acceptable at all. The button says so, so nobody
 * clicks it thinking it is a free look.
 */
function RevealAndCopy({
  masked,
  field,
  label,
  vendor_id,
}: {
  masked: string | null;
  field: 'upi_id' | 'account_number';
  label: string;
  vendor_id: string;
}) {
  const reveal = use_reveal_vendor_payout(vendor_id);
  if (!masked) return null;

  function copy() {
    reveal.mutate(undefined, {
      onSuccess: (secret) => {
        const value = secret[field];
        if (!value) {
          notify.error(new Error(`This vendor has no ${label.toLowerCase()} on file`));
          return;
        }
        void navigator.clipboard.writeText(value);
        notify.success(`${label} copied`, {
          description: 'Recorded against your account.',
        });
      },
      onError: (err) => notify.error(err, `Could not read the ${label.toLowerCase()}`),
    });
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular">{masked}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={copy}
            disabled={reveal.isPending}
            aria-label={`Copy full ${label}`}
            data-testid={`payout-copy-${field}`}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            {reveal.isPending ? (
              <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Copy aria-hidden="true" className="h-3.5 w-3.5" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          Copies the full {label.toLowerCase()} to your clipboard. Recorded against your account.
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

function PayoutCard({
  vendor,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
}) {
  const p = vendor.payout;
  const [editing_payout, set_editing_payout] = useState(false);
  const request_payout = use_request_vendor_payout(vendor.id);

  // Mirrors the server's own 24-hour gap, so the button is disabled rather than
  // refusing after the click.
  const asked_recently =
    vendor.payout_requested_at !== null &&
    Date.now() - new Date(vendor.payout_requested_at).getTime() < 24 * 60 * 60 * 1000;

  function send_request() {
    request_payout.mutate(undefined, {
      onSuccess: () =>
        notify.success('Payout request sent', {
          description: 'The vendor has been notified in their app to add bank or UPI details.',
        }),
      onError: (err) => notify.error(err, 'Could not send the payout request'),
    });
  }

  function on_date(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  return (
    <Card data-testid="vendor-payout-card">
      <CardHeader>
        <CardTitle>Payout details</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        {!p ? (
          <div className="flex flex-col gap-3" data-testid="vendor-payout-missing">
            <div className="rounded-panel bg-warning/10 px-3 py-2.5 text-data text-warning">
              <p className="font-semibold">
                No payout details on file — this vendor cannot be paid.
              </p>
              <p className="mt-1 text-warning/80">
                Only possible for accounts created before payout became a required onboarding step.
                Everything they earn is accruing with nowhere to settle to.
              </p>
            </div>
            {/*
              Already asked, and by whom.

              Without this every operator who opened the vendor would send another
              push, because nothing on the page would say the last one had. The
              server enforces the same 24-hour gap; this is so the button never
              refuses as a surprise.
            */}
            {vendor.payout_requested_at && (
              <p className="text-data text-muted-foreground" data-testid="payout-already-requested">
                Asked {ago(vendor.payout_requested_at)}
                {vendor.payout_requested_by_name ? ` by ${vendor.payout_requested_by_name}` : ''}.
                {asked_recently && ' They can be asked again tomorrow.'}
              </p>
            )}

            {editing_payout ? (
              <SetPayoutForm
                vendor={vendor}
                on_done={() => set_editing_payout(false)}
                on_cancel={() => set_editing_payout(false)}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {/*
                  The primary action, deliberately. In almost every case the
                  vendor should type their own bank details — this just tells
                  them to, since payout became mandatory after they onboarded and
                  nothing in their app has ever asked.
                */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="sm"
                        disabled={asked_recently || request_payout.isPending}
                        onClick={send_request}
                        data-testid="payout-request"
                      >
                        <Send className="h-4 w-4" />
                        {request_payout.isPending
                          ? 'Sending…'
                          : vendor.payout_requested_at
                            ? 'Ask again'
                            : 'Ask vendor to add them'}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {asked_recently
                      ? 'Already asked in the last 24 hours — this sends a push to a real business.'
                      : 'Notifies the vendor in their app to add bank or UPI details.'}
                  </TooltipContent>
                </Tooltip>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => set_editing_payout(true)}
                  data-testid="payout-add"
                >
                  <Wallet className="h-4 w-4" /> Enter them myself
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Row label="Method" value={p.method === 'UPI' ? 'UPI' : 'Bank account'} />
            {p.method === 'UPI' ? (
              <Row
                label="UPI ID"
                value={
                  <RevealAndCopy
                    masked={p.upi_id_display}
                    field="upi_id"
                    label="UPI ID"
                    vendor_id={vendor.id}
                  />
                }
              />
            ) : (
              <>
                <Row label="Account holder" value={p.account_holder_name} />
                <Row
                  label="Account"
                  value={
                    <RevealAndCopy
                      masked={p.account_number_last4 ? `****${p.account_number_last4}` : null}
                      field="account_number"
                      label="Account number"
                      vendor_id={vendor.id}
                    />
                  }
                />
                <Row label="IFSC" value={p.ifsc_code} />
              </>
            )}
            <Row label="Verified" value={p.verified_at ? on_date(p.verified_at) : 'Not verified'} />
            <Row
              label="Times changed"
              value={
                p.change_count === 0
                  ? 'Never changed since onboarding'
                  : `${p.change_count} — last on ${on_date(p.last_changed_at)}`
              }
            />
            <Row
              label="Next change"
              value={
                p.can_edit
                  ? 'Allowed now'
                  : `Locked until ${on_date(p.next_editable_at)} (${p.cooldown_days}-day window)`
              }
            />
            <p className="pt-3 text-xs text-muted-foreground">
              Vendors may change these once every {p.cooldown_days} days and we are notified each
              time. The full account number or UPI ID is encrypted and never shown on this page —
              Copy puts it straight on your clipboard, and each copy is recorded against your
              account.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Enter payout details for a vendor that has none.
 *
 * Deliberately create-only, matching the server: re-pointing an existing payout
 * destination is the fraud-shaped operation and stays with the vendor, in their
 * own app, behind their own cooldown. This exists because payout became a
 * required onboarding step after the platform already had vendors, so a large
 * number of verified businesses are trading with nowhere to settle to and no
 * screen of their own that can fix it.
 *
 * The copy says to prefer the vendor doing it themselves, because an admin
 * typing someone else's bank details should feel like the exception it is.
 */
function SetPayoutForm({
  vendor,
  on_done,
  on_cancel,
}: {
  vendor: NonNullable<ReturnType<typeof use_vendor_detail>['data']>;
  on_done: () => void;
  on_cancel: () => void;
}) {
  const set_payout = use_set_vendor_payout(vendor.id);
  const [method, set_method] = useState<'BANK_ACCOUNT' | 'UPI'>('BANK_ACCOUNT');
  const [holder, set_holder] = useState('');
  const [account, set_account] = useState('');
  const [ifsc, set_ifsc] = useState('');
  const [upi, set_upi] = useState('');
  const [error, set_error] = useState<string | null>(null);

  // Mirrors the server's validators, so a typo is caught before it becomes a
  // round trip and a red toast.
  const ifsc_ok = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase());
  const account_ok = /^[0-9]{6,20}$/.test(account);
  const upi_ok = /^[\w.\-]{2,256}@[a-zA-Z][a-zA-Z0-9]{1,63}$/.test(upi);
  const can_submit =
    method === 'BANK_ACCOUNT' ? holder.trim().length >= 2 && account_ok && ifsc_ok : upi_ok;

  function submit() {
    set_error(null);
    const input =
      method === 'BANK_ACCOUNT'
        ? {
            method: 'BANK_ACCOUNT' as const,
            account_holder_name: holder.trim(),
            account_number: account,
            ifsc_code: ifsc.toUpperCase(),
          }
        : { method: 'UPI' as const, upi_id: upi.trim() };

    set_payout.mutate(input, {
      onSuccess: () => {
        notify.success('Payout details saved', {
          description:
            'The vendor can be settled from the next run, and can still correct these from their own app.',
        });
        on_done();
      },
      onError: (err) => {
        const message = err instanceof ApiError ? err.message : 'Could not save payout details';
        set_error(message);
        notify.error(err, message);
      },
    });
  }

  return (
    <div className="flex flex-col gap-3" data-testid="set-payout-form">
      <p className="rounded-panel bg-info/10 p-2.5 text-data text-info">
        Where possible, ask the vendor to add these in their own app — they can see and correct them
        there. Use this for legacy accounts that predate the payout step.
      </p>

      <div className="flex flex-col gap-1">
        <label
          className="text-micro font-bold uppercase tracking-wide text-muted-foreground"
          htmlFor="payout-method"
        >
          Method
        </label>
        <select
          id="payout-method"
          className="console-select w-48"
          value={method}
          onChange={(e) => set_method(e.target.value as 'BANK_ACCOUNT' | 'UPI')}
          data-testid="payout-method"
        >
          <option value="BANK_ACCOUNT">Bank account</option>
          <option value="UPI">UPI</option>
        </select>
      </div>

      {method === 'BANK_ACCOUNT' ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="payout-holder">Account holder name</Label>
            <Input
              id="payout-holder"
              value={holder}
              onChange={(e) => set_holder(e.target.value)}
              placeholder="As printed on the passbook"
              className="console-field"
              data-testid="payout-holder"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="payout-account">Account number</Label>
            <Input
              id="payout-account"
              value={account}
              inputMode="numeric"
              onChange={(e) => set_account(e.target.value.replace(/\D/g, ''))}
              placeholder="6–20 digits"
              className="console-field tabular"
              data-testid="payout-account"
            />
            {account.length > 0 && !account_ok && (
              <p className="text-micro text-destructive">Must be 6–20 digits.</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="payout-ifsc">IFSC code</Label>
            <Input
              id="payout-ifsc"
              value={ifsc}
              onChange={(e) => set_ifsc(e.target.value.toUpperCase())}
              placeholder="HDFC0001234"
              className="console-field tabular"
              data-testid="payout-ifsc"
            />
            {ifsc.length > 0 && !ifsc_ok && (
              <p className="text-micro text-destructive">
                Four letters, a zero, then six characters.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex max-w-sm flex-col gap-1">
          <Label htmlFor="payout-upi">UPI ID</Label>
          <Input
            id="payout-upi"
            value={upi}
            onChange={(e) => set_upi(e.target.value)}
            placeholder="name@bank"
            className="console-field"
            data-testid="payout-upi"
          />
          {upi.length > 0 && !upi_ok && (
            <p className="text-micro text-destructive">Must look like name@bank.</p>
          )}
        </div>
      )}

      {error && (
        <p className="text-data text-destructive" data-testid="payout-error">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!can_submit || set_payout.isPending}
          onClick={submit}
          data-testid="payout-save"
        >
          {set_payout.isPending ? 'Saving…' : 'Save payout details'}
        </Button>
        <Button size="sm" variant="ghost" onClick={on_cancel} data-testid="payout-cancel">
          Cancel
        </Button>
      </div>
    </div>
  );
}
